import { useState } from 'react';
import { useNavigate } from 'react-router';
import { contractorApi } from '../../utils/installerApi';
import { useContractorSession } from '../../hooks/useContractorSession';
import { InstallerTopbar } from '../installer/InstallerChrome';
import '../../styles/InstallerPages.css';

function faError(e) {
  const m = (e && e.message) || String(e);
  if (/failed to fetch|load failed|networkerror|err_network|connection/i.test(m)) {
    return 'ارتباط با سرور برقرار نشد. اتصال اینترنت خود را بررسی کنید و دوباره تلاش کنید.';
  }
  return m;
}

// Unified phone-first auth for contractors (پیمانکار), mirroring InstallerAuthPage:
// ONE phone + ONE OTP, then an existing contractor is logged in and routed by
// status, while a new one continues to a light company-profile form (no document
// uploads - the admin approves on the company details).
export default function ContractorAuthPage() {
  const nav = useNavigate();
  const { setToken } = useContractorSession();
  const [stage, setStage] = useState('phone'); // 'phone' | 'otp' | 'profile'
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [signupTicket, setSignupTicket] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ company_name: '', contact_name: '', contact_phone: '', address_text: '' });

  const guard = (fn) => async () => {
    setErr(null); setBusy(true);
    try { await fn(); } catch (e) { setErr(faError(e)); } finally { setBusy(false); }
  };

  const sendOtp = guard(async () => {
    if (!phone.trim()) throw new Error('شماره موبایل را وارد کنید.');
    await contractorApi.requestOtp(phone);
    setSignupTicket(null);
    setStage('otp');
  });

  const verify = guard(async () => {
    const r = await contractorApi.verifyOtp(phone, otp);
    if (r.exists) {
      setToken(r.session_token);
      nav(r.status === 'approved' ? '/contractor/dashboard' : '/contractor/pending');
    } else {
      setSignupTicket(r.signup_ticket);
      setStage('profile');
    }
  });

  const submitProfile = guard(async () => {
    if (!form.company_name.trim()) throw new Error('نام شرکت الزامی است.');
    if (!signupTicket) {
      setStage('phone');
      throw new Error('مجوز ثبت‌نام منقضی شده است. لطفاً دوباره کد تایید دریافت کنید.');
    }
    let r;
    try {
      r = await contractorApi.signup({
        signup_ticket: signupTicket, phone, company_name: form.company_name.trim(),
        contact_name: form.contact_name, contact_phone: form.contact_phone, address_text: form.address_text,
      });
    } catch (e) {
      setSignupTicket(null); setOtp(''); setStage('phone');
      const message = faError(e);
      throw new Error(/کد تایید جدید/.test(message) ? message : `${message} برای تلاش دوباره، کد تایید جدید دریافت کنید.`);
    }
    setToken(r.session_token);
    nav('/contractor/pending');
  });

  return (
    <div className="inst">
      <div className="inst-shell">
        <InstallerTopbar sub={stage === 'profile' ? 'ثبت‌نام شرکت پیمانکار' : 'ورود شرکت پیمانکار'} />
        <div className="inst-card">
          {err && <div role="alert" className="inst-alert">{err}</div>}

          {stage === 'phone' && (
            <>
              <h1 className="inst-h1">ورود شرکت پیمانکار</h1>
              <p className="inst-lead">شماره موبایل خود را وارد کنید. اگر حساب نداشته باشید، در همین مسیر ثبت‌نام می‌شوید.</p>
              <div className="inst-field">
                <label className="inst-label" htmlFor="phone">شماره موبایل</label>
                <input className="inst-input" id="phone" inputMode="numeric" value={phone}
                  onChange={(e) => setPhone(e.target.value)} placeholder="09xxxxxxxxx" />
              </div>
              <button className="inst-btn" disabled={busy} onClick={sendOtp}>
                {busy && <span className="inst-spin" />}ادامه
              </button>
            </>
          )}

          {stage === 'otp' && (
            <>
              <h1 className="inst-h1">کد تایید</h1>
              <p className="inst-lead">کد ارسال‌شده به شماره {phone} را وارد کنید.</p>
              <div className="inst-field">
                <label className="inst-label" htmlFor="otp">کد تایید</label>
                <input className="inst-input inst-otp" id="otp" inputMode="numeric" maxLength={4} value={otp}
                  onChange={(e) => setOtp(e.target.value)} placeholder="----" />
              </div>
              <div className="inst-btnrow">
                <button className="inst-btn inst-btn-ghost" disabled={busy} onClick={() => { setOtp(''); setStage('phone'); }}>بازگشت</button>
                <button className="inst-btn" disabled={busy} onClick={verify}>
                  {busy && <span className="inst-spin" />}تایید
                </button>
              </div>
            </>
          )}

          {stage === 'profile' && (
            <>
              <h1 className="inst-h1">تکمیل ثبت‌نام شرکت</h1>
              <p className="inst-lead">شماره شما تایید شد. اطلاعات شرکت برای بررسی و تایید استفاده می‌شود.</p>
              <div className="inst-field">
                <label className="inst-label" htmlFor="company">نام شرکت شرکت پیمانکار</label>
                <input className="inst-input" id="company" value={form.company_name}
                  onChange={(e) => setForm({ ...form, company_name: e.target.value })} placeholder="مثلاً فیبر گستر پارس" />
              </div>
              <div className="inst-field">
                <label className="inst-label" htmlFor="cname">نام مسئول</label>
                <input className="inst-input" id="cname" value={form.contact_name}
                  onChange={(e) => setForm({ ...form, contact_name: e.target.value })} placeholder="نام و نام خانوادگی مسئول" />
              </div>
              <div className="inst-field">
                <label className="inst-label" htmlFor="cphone">تلفن تماس</label>
                <input className="inst-input" id="cphone" inputMode="numeric" value={form.contact_phone}
                  onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} placeholder="تلفن ثابت یا همراه" />
              </div>
              <div className="inst-field">
                <label className="inst-label" htmlFor="addr">آدرس دفتر</label>
                <input className="inst-input" id="addr" value={form.address_text}
                  onChange={(e) => setForm({ ...form, address_text: e.target.value })} placeholder="آدرس متنی" />
              </div>
              <button className="inst-btn" disabled={busy} onClick={submitProfile}>
                {busy && <span className="inst-spin" />}ثبت‌نام
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
